import React, { useCallback, useEffect, useState } from "react";
import { Award, CheckCircle2, Download, Loader2, Lock, RefreshCw, ShieldAlert } from "lucide-react";

import {
    downloadLearnerCertificateApi,
    fetchLearnerCertificateStatusApi,
    generateLearnerCertificateApi,
    previewLearnerCertificateApi,
} from "../../../../services/moduleCertificateApi";
import "./CertificateContent.css";

const extractMessage = (error, fallback) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

const formatDate = (value) => {
    if (!value) return null;
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(value));
    } catch {
        return value;
    }
};

const saveBlob = (response, fallbackName) => {
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default function CertificateContent({ moduleId, content, onRefreshPlayer }) {
    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const loadStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchLearnerCertificateStatusApi(moduleId, content.id);
            setCertificate(response.data);
            if (response.data?.status === "GENERATED") {
                onRefreshPlayer?.();
            }
        } catch (requestError) {
            setError(extractMessage(requestError, "Impossible de charger le statut du certificat."));
        } finally {
            setLoading(false);
        }
    }, [content.id, moduleId, onRefreshPlayer]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        const loadPreview = async () => {
            if (certificate?.status !== "GENERATED") {
                setPreviewUrl(null);
                setPreviewError(null);
                return;
            }
            setPreviewLoading(true);
            setPreviewError(null);
            setPreviewUrl(null);
            try {
                const response = await previewLearnerCertificateApi(moduleId, content.id);
                if (cancelled) return;
                objectUrl = URL.createObjectURL(response.data);
                setPreviewUrl(objectUrl);
            } catch (requestError) {
                if (!cancelled) {
                    setPreviewUrl(null);
                    setPreviewError(extractMessage(requestError, "Apercu PDF indisponible pour ce certificat."));
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [certificate?.status, content.id, moduleId]);

    const handleGenerate = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const response = await generateLearnerCertificateApi(moduleId, content.id);
            setCertificate(response.data?.certificate || response.data);
            onRefreshPlayer?.();
        } catch (requestError) {
            setError(extractMessage(requestError, "La generation du certificat a echoue."));
            await loadStatus();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownload = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const response = await downloadLearnerCertificateApi(moduleId, content.id);
            saveBlob(response, `${content.title || "certificat"}.pdf`);
        } catch (requestError) {
            setError(extractMessage(requestError, "Le telechargement du certificat a echoue."));
        } finally {
            setActionLoading(false);
        }
    };

    const status = certificate?.status || "GENERATING";
    const generatedAt = formatDate(certificate?.generatedAt);
    const remaining = certificate?.remainingRequiredContentCount || 0;
    const required = certificate?.requiredContentCount || 0;
    const completed = certificate?.completedRequiredContentCount || 0;

    if (loading) {
        return (
            <div className="certificate-card certificate-card--loading">
                <Loader2 className="certificate-spin" size={24} />
                <span>Chargement du certificat...</span>
            </div>
        );
    }

    return (
        <div className={`certificate-card certificate-card--${status.toLowerCase()}`}>
            <div className="certificate-hero">
                <div className="certificate-icon">
                    {status === "LOCKED" ? <Lock size={30} /> : status === "ERROR" ? <ShieldAlert size={30} /> : <Award size={30} />}
                </div>
                <div>
                    <span className="certificate-kicker">Certificat</span>
                    <h3>{certificate?.certificatName || content.title}</h3>
                    <p>{certificate?.reason || "Votre certificat sera disponible lorsque les conditions seront remplies."}</p>
                </div>
            </div>

            {status === "LOCKED" ? (
                <div className="certificate-progress">
                    <div>
                        <strong>{remaining}</strong>
                        <span>contenu{remaining > 1 ? "s" : ""} restant{remaining > 1 ? "s" : ""}</span>
                    </div>
                    <div className="certificate-bar" aria-hidden="true">
                        <span style={{ width: `${required > 0 ? Math.round((completed * 100) / required) : 0}%` }} />
                    </div>
                    <small>{completed}/{required} requis terminés</small>
                </div>
            ) : null}

            {status === "GENERATED" ? (
                <>
                    <div className="certificate-generated">
                        <CheckCircle2 size={18} />
                        <span>{generatedAt ? `Genere le ${generatedAt}` : "Certificat genere"}</span>
                    </div>
                    <div className="certificate-preview">
                        {previewLoading ? (
                            <div className="certificate-preview-state">
                                <Loader2 className="certificate-spin" size={22} />
                                <span>Chargement de l'apercu PDF...</span>
                            </div>
                        ) : previewUrl ? (
                            <iframe title={`Apercu certificat ${certificate?.certificatName || content.title}`} src={previewUrl} />
                        ) : (
                            <div className="certificate-preview-state certificate-preview-state--error">
                                {previewError || "Apercu PDF non disponible. Vous pouvez telecharger le certificat."}
                            </div>
                        )}
                    </div>
                </>
            ) : null}

            {status === "ERROR" || error ? (
                <div className="certificate-error">
                    {error || certificate?.errorMessage || "Une erreur est survenue pendant la generation."}
                </div>
            ) : null}

            <div className="certificate-actions">
                {status === "AVAILABLE" || status === "ERROR" ? (
                    <button className="cr-btn cr-btn--primary" type="button" onClick={handleGenerate} disabled={actionLoading}>
                        {actionLoading ? <Loader2 className="certificate-spin" size={16} /> : <Award size={16} />}
                        Generer mon certificat
                    </button>
                ) : null}
                {status === "GENERATED" ? (
                    <button className="cr-btn cr-btn--primary" type="button" onClick={handleDownload} disabled={actionLoading}>
                        {actionLoading ? <Loader2 className="certificate-spin" size={16} /> : <Download size={16} />}
                        Telecharger
                    </button>
                ) : null}
                <button className="cr-btn cr-btn--secondary" type="button" onClick={loadStatus} disabled={actionLoading}>
                    <RefreshCw size={16} />
                    Actualiser
                </button>
            </div>
        </div>
    );
}
